import { tMinutes } from '../../lib/minutes-i18n';
import { noTextReason, retryingChunks } from './awaiting-summary';
import type { VoiceSessionItem } from '../../api/voice-sessions';

/**
 * 「요약하기」를 누른 뒤 ★ **하는 말** — ⭐ 다섯 갈래를 «한 곳»에 둔다 (2026-10-09).
 *
 * ## ⛔ 왜 떼어냈나
 *
 * 2026-10-08 사용자 결정으로 ★ 같은 단추가 ⭐ **두 자리**에 선다:
 *
 *     「정리 대기」 목록 줄   AwaitingSummaryRow      ← 2026-10-04 부터 있었다
 *     상세의 「요약」 탭      MinutesSummaryPanel     ← 2026-10-09 (시안 3절)
 *
 * ⇒ ⛔ 말을 **두 벌로 적으면** ★ 언젠가 갈린다 — 이 레포가 겪은 「★ 같은 뜻을 ⭐ 다른 글자로」 그 꼴이다.
 * ⇒ ⇒ ★ 그래서 ⭐ 다섯 갈래의 **열쇠 고르기 · `role` · 경고 꼴**을 ★ 여기 한 곳에 둔다.
 *
 * ## ⚠ `role` 이 ★ 갈래마다 ⭐ 다르다 — 그것이 «뜻»이다
 *
 *     queued         status   ⇐ 소식이다
 *     no_text        status   ⇐ ⭐ **고장이 아니다** — 그 녹음의 «사실»이다(조용한 녹음이거나 전사가 글자를 못 만든 것)
 *     cannot_finish  alert    ⇐ ⛔ «멈춘다»는 소식이다
 *     failed · gone  alert
 *
 * ⇒ ⛔ 그래서 `role` 을 하나로 묶지 않았다. 이 가름은 ★ 2026-10-05·10-07 주석이 적어 둔 것이다.
 *
 * ## ⛔ `listHint` 는 걷었다 (2026-10-20 사용자 「걷어라」)
 *
 * 「시작했습니다」 뒤에 「정리가 끝나면 **이 목록에** 회의록으로 올라옵니다 …」(`minutes.awaiting.queued_hint`)를
 * 붙이던 갈래였다 — 그 말은 «목록 줄»에서만 참이라 부르는 쪽이 문맥을 말했다(`listHint`).
 * ⇒ ④ 후 A(2026-10-20 · 목록 줄에 「요약하기」 없음)로 그 자리가 없어져 남은 호출은 상세(늘 꺼짐)뿐이었다.
 * ⇒ 갈래 · 속성 · 열쇠를 함께 걷었다. 앱이 그 열쇠를 읽어 맞대던 짝(`MinutesAwaitingText.QUEUED_HINT`)도 AOS 에서 함께 걷는다.
 * ⇒ ⚠ 상세에서 「이 목록에 …」를 다시 말하지 않는다 — 거기는 목록이 아니다(minutes-summary-run.test.tsx ⓕ).
 * ⇒ ⚠ 상세 쪽의 한 줄(「요약이 완료되면 알려드립니다.」)은 여전히 **A-2.5** 몫이다.
 */
/* ⭐ 2026-10-20 (시안 ②) — 'retrying' = 다시 받아 적는 조각이 남아 서버를 «안» 불렀다. */
export type SummarizeSaid = 'queued' | 'failed' | 'gone' | 'no_text' | 'cannot_finish' | 'retrying';

export function MinutesSummarizeSay({ said, s }: {
  said: SummarizeSaid | null;
  /** 말을 꾸미는 데 쓰는 사실 — ⛔ 판정이 아니다(판정은 `awaiting-summary.ts` 한 곳이다). */
  s: Pick<VoiceSessionItem, 'failed_chunks' | 'received_chunks' | 'transcribed_chunks' | 'gave_up_chunks'>;
}): JSX.Element | null {
  if (said === null) return null;

  if (said === 'queued') {
    return (
      <span className="minutes-awaiting-say" role="status">
        {tMinutes('minutes.awaiting.queued')}
        {/* ⭐ 2026-10-20 (시안 ②) — 포기한 조각이 있으면 «빼고 요약한다»를 함께 말한다(서버가 그 조각을 건너뛴다). */}
        {(s.gave_up_chunks ?? 0) > 0 ? ` ${tMinutes('minutes.awaiting.queued_skip', { n: s.gave_up_chunks ?? 0 })}` : ''}
      </span>
    );
  }

  /*
    ⭐ 2026-10-20 (시안 ②) — 다시 받아 적는 조각이 남았다. ⚠ `role="status"` — 고장이 아니라 «기다리면 된다»는 소식이다.
    ⚠ 수는 «보여 주는» 것이고 판정이 아니다 — 판정은 awaiting-summary.ts `failedChunkState` 한 곳이다.
  */
  if (said === 'retrying') {
    return (
      <span className="minutes-awaiting-say" role="status">
        {tMinutes('minutes.awaiting.retrying', { n: retryingChunks(s) })}
      </span>
    );
  }

  if (said === 'failed') {
    return (
      <span className="minutes-awaiting-say minutes-awaiting-say--warn" role="alert">
        {tMinutes('minutes.awaiting.failed')}
      </span>
    );
  }

  if (said === 'gone') {
    return (
      <span className="minutes-awaiting-say minutes-awaiting-say--warn" role="alert">
        {tMinutes('minutes.awaiting.gone')}
      </span>
    );
  }

  /*
    ⭐ 2026-10-07 — 「요약이 끝까지 가지 못합니다」. ⚠ 여기는 `role="alert"` 다 —
      0자(㉠)는 «그 녹음의 사실»이라 `status` 였지만, 이것은 ⛔ «멈춘다»는 소식이다.
    ⚠ 말에 쓰는 `s.failed_chunks` 는 «수를 보여 주는» 것이고 ⛔ 판정이 아니다 —
      판정은 `summaryWouldStall` 한 곳이다(부르는 쪽의 `press()` 주석).
  */
  if (said === 'cannot_finish') {
    return (
      <span className="minutes-awaiting-say minutes-awaiting-say--warn" role="alert">
        {tMinutes('minutes.awaiting.cannot_finish')}{' '}
        {tMinutes('minutes.awaiting.cannot_finish_why', { n: s.failed_chunks })}
      </span>
    );
  }

  /*
    ⭐ ㉠ — 「요약할 글이 없습니다」. ⚠ `role="alert"` 가 아니라 `role="status"` 다 —
      이것은 **고장이 아니고** 그 녹음의 사실이다(조용한 녹음이거나 전사가 글자를 못 만든 것).
  */
  return (
    <span className="minutes-awaiting-say minutes-awaiting-say--warn" role="status">
      {tMinutes('minutes.awaiting.no_text')}{' '}
      {tMinutes(`minutes.awaiting.no_text_${noTextReason(s as VoiceSessionItem)}` as const)}
    </span>
  );
}
